'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Home, MapPin, Refrigerator, Ruler, Thermometer, Ticket, Timer, type LucideIcon } from 'lucide-react'
import type { PressCardImageData } from '@/lib/press-card-image'

const TOPICS: Record<string, { label: string; Icon: LucideIcon }> = {
  'appliance-lifespan-index-2026': { label: 'Appliance lifespan', Icon: Timer },
  'kitchen-space-report-2026': { label: 'Kitchen space', Icon: Ruler },
  'utility-rebate-database-2026': { label: 'Utility rebates', Icon: Ticket },
  'how-america-refrigerates-2026': { label: 'How America refrigerates', Icon: Refrigerator },
  'freezer-economy-2026': { label: 'The freezer economy', Icon: Thermometer },
  'energy-star-report-card-2026': { label: 'Energy efficiency', Icon: Thermometer },
  'fridge-inequality-index-2026': { label: 'The appliance cost gap', Icon: Home },
  'landlord-fridge-problem-2026': { label: 'Rental homes', Icon: Home },
  'kitchen-climate-divide-2026': { label: 'Kitchen climate', Icon: Thermometer },
  'rebate-desert-states-2026': { label: 'Rebate deserts', Icon: Ticket },
  '2026-cold-standard-rankings-rural': { label: 'Small towns', Icon: MapPin },
}

/** Stable report cover when a release has no suitable image or its image fails. */
export function PressCardImage({ image, slug }: { image: PressCardImageData | null; slug: string }) {
  const [failedSource, setFailedSource] = useState<string | null>(null)
  const topic = TOPICS[slug] ?? { label: 'Original research', Icon: Refrigerator }
  const Icon = topic.Icon
  return (
    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-gray-100 bg-white mb-4" data-press-card-image={image && failedSource !== image.src ? 'release' : 'branded'}>
      {/* Optimized, not `unoptimized` (09/17/2026, FRIDGE-1031): the two
          wire-hosted release figures are 4.2 MB and 1.6 MB PNGs, so the index
          card showed an empty white frame until they arrived.
          img.einpresswire.com is in lib/image-host-allowlist.js, so the
          optimizer resizes them to the card's real width as AVIF/WebP. */}
      {image && failedSource !== image.src ? (
        <Image src={image.src} alt={image.alt} fill sizes="(max-width: 639px) 100vw, 50vw" className="object-contain p-2" onError={() => setFailedSource(image.src)} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-between gap-4 p-5 sm:p-7" style={{ background: 'linear-gradient(120deg, white 50%, var(--blue-soft, #edf5ff) 100%)' }} aria-hidden="true">
          <div className="min-w-0">
            {/* FRIDGE-1540: the file's aspect ratio inline, so Chromium counts this lazy logo as sized; the drawing is unchanged. */}
            <Image src="/Fridge.com-brand-logos/Fridge.com-blue.png" alt="" width={113} height={20} className="mb-4 h-auto w-28" style={{ aspectRatio: '2215 / 391' }} />
            <span className="block text-[10px] font-semibold uppercase tracking-widest text-gray-500">Research report</span>
            <span className="mt-2 block text-lg sm:text-2xl font-semibold leading-tight text-gray-900">{topic.label}</span>
          </div>
          <Icon className="h-16 w-16 sm:h-20 sm:w-20 shrink-0" style={{ color: 'var(--fridge-orange)' }} strokeWidth={1.25} />
        </div>
      )}
    </div>
  )
}
